import React, { useState, useEffect } from 'react';
import { useNotifications } from '../hooks/useNotifications';
import { useToast } from './ToastProvider';

const NotificationSettings = ({ isOpen, onClose }) => {
  const { 
    permissionStatus, 
    requestPermission, 
    isEnabled, 
    isSupported,
    showCustomNotification 
  } = useNotifications();
  const { showToast } = useToast();
  const [isRequesting, setIsRequesting] = useState(false);

  const handleEnableNotifications = async () => {
    setIsRequesting(true);
    try {
      const granted = await requestPermission();
      if (granted) {
        showToast('Notifications enabled successfully!', 'success');
        // Show test notification
        showCustomNotification(
          'Notifications Enabled', 
          'You will now receive message notifications', 
          { autoClose: 3000 }
        );
      } else {
        showToast('Notification permission denied', 'error');
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
      showToast('Failed to enable notifications', 'error');
    } finally {
      setIsRequesting(false);
    }
  };

  const handleTestNotification = () => {
    if (isEnabled) {
      showCustomNotification(
        'Test Notification',
        'This is a test message to verify notifications are working correctly.',
        { autoClose: 4000 }
      );
      showToast('Test notification sent!', 'info');
    }
  };

  const getStatusColor = () => {
    if (!isSupported) return '#ef4444'; // red
    if (permissionStatus.permission === 'granted') return '#10b981'; // green
    if (permissionStatus.permission === 'denied') return '#ef4444'; // red
    return '#f59e0b'; // yellow
  };

  const getStatusText = () => {
    if (!isSupported) return 'Not Supported';
    if (permissionStatus.permission === 'granted') return 'Enabled';
    if (permissionStatus.permission === 'denied') return 'Blocked';
    return 'Not Enabled';
  };

  if (!isOpen) return null;

  return (
    <div className="modal_overlay">
      <div className="modal_content notification_settings_modal">
        <div className="modal_header">
          <h3>Notification Settings</h3>
          <button className="close_btn" onClick={onClose}>
            ×
          </button>
        </div>
        
        <div className="modal_body">
          <div className="notification_section">
            <div className="setting_item">
              <div className="setting_info">
                <h4>Browser Notifications</h4>
                <p>Get notified when you receive new messages, even when the tab is not active.</p>
              </div>
              <div className="setting_status">
                <span 
                  className="status_indicator"
                  style={{ backgroundColor: getStatusColor() }}
                ></span>
                <span className="status_text">{getStatusText()}</span>
              </div>
            </div>

            <div className="setting_actions">
              {!isSupported && (
                <div className="warning_message">
                  <p>⚠️ Your browser does not support notifications</p>
                </div>
              )}

              {isSupported && permissionStatus.permission === 'default' && (
                <button 
                  className="btn_primary"
                  onClick={handleEnableNotifications}
                  disabled={isRequesting}
                >
                  {isRequesting ? 'Requesting...' : 'Enable Notifications'}
                </button>
              )}

              {isSupported && permissionStatus.permission === 'denied' && (
                <div className="info_message">
                  <p>
                    Notifications are blocked. To enable them:
                    <br />
                    1. Click the lock icon in your browser's address bar
                    <br />
                    2. Allow notifications for this site
                    <br />
                    3. Refresh the page
                  </p>
                </div>
              )}

              {isEnabled && (
                <div className="enabled_actions">
                  <button 
                    className="btn_secondary"
                    onClick={handleTestNotification}
                  >
                    Test Notification
                  </button>
                  <div className="info_message">
                    <p>✅ Notifications are enabled and working!</p>
                  </div>
                </div>
              )}
            </div>

            <div className="notification_info">
              <h4>How notifications work:</h4>
              <ul>
                <li>You'll receive notifications for new messages when this tab is not active</li>
                <li>Notifications include the sender name, room name, and message preview</li>
                <li>Click on a notification to open the chat room</li>
                <li>Notifications automatically disappear after 5 seconds</li>
                <li>Image messages show as "📷 Image" with file type</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="modal_footer">
          <button className="btn_secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;